import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  Backdrop,
} from "@mui/material";
import { useSpring, animated } from "@react-spring/web";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import useAuthFlow from "../../Hooks/use-auth-flow";

const SESSION_EXPIRED_EVENT = "session-expired";

const Fade = React.forwardRef(function Fade(props, ref) {
  const { children, in: open, onEnter, onExited, ...other } = props;
  const style = useSpring({
    from: { opacity: 0 },
    to: { opacity: open ? 1 : 0 },
    onStart: () => open && onEnter?.(null, true),
    onRest: () => !open && onExited?.(null, true),
  });
  return (
    <animated.div ref={ref} style={style} {...other}>
      {children}
    </animated.div>
  );
});

export const dispatchSessionExpired = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
  }
};

const SessionExpiredModal = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleSessionExpired = () => setOpen(true);
    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () =>
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, []);

  const { triggerAuth } = useAuthFlow();

  const handleLoginAgain = () => {
    setOpen(false);
    triggerAuth("signin")
  };

  return (
    <Modal
      open={open}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          TransitionComponent: Fade,
          sx: { backgroundColor: "rgba(0,0,0,0.5)" },
        },
      }}
      sx={{ zIndex: 9999 }}
    >
      <Fade in={open}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 380 },
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              bgcolor: "rgba(123, 64, 140, 0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <LogoutRoundedIcon
              sx={{ fontSize: 36, color: "primary.main" }}
              aria-hidden
            />
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#1a1a1a",
              mb: 1,
              fontSize: "1.25rem",
            }}
          >
            Session expired
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#666",
              mb: 3,
              lineHeight: 1.5,
              px: 1,
            }}
          >
            Your session has expired for your security. Please log in again to
            continue.
          </Typography>
          <Button
            variant="contained"
            fullWidth
            onClick={handleLoginAgain}
            sx={{
              py: 1.5,
              fontWeight: 600,
              textTransform: "none",
              fontSize: "1rem",
            }}
          >
            Log in again
          </Button>
        </Box>
      </Fade>
    </Modal>
  );
};

export default SessionExpiredModal;
